<template>
  <page-header-wrapper>
    <a-card :bordered="false" title="保险订单">
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="6" :sm="24">
              <a-form-item label="保险方案">
                <a-input
                  v-model="queryParams.name"
                  placeholder="请输入保险方案名称"
                  allow-clear
                  @press-enter="handleQuery"
                />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="支付状态">
                <a-select
                  v-model="queryParams.bondPayStatus"
                  placeholder="请选择支付状态"
                  allow-clear
                  style="width: 160px"
                >
                  <a-select-option :value="1">待支付</a-select-option>
                  <a-select-option :value="2">已支付</a-select-option>
                  <a-select-option :value="3">已取消</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="投保状态">
                <a-select
                  v-model="queryParams.underwritingStatus"
                  placeholder="请选择投保状态"
                  allow-clear
                  style="width: 180px"
                >
                  <a-select-option v-for="item in underwritingStatusOptions" :key="item.value" :value="item.value">
                    {{ item.label }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="订单号">
                <a-input
                  v-model="queryParams.customerOrderId"
                  placeholder="请输入订单号"
                  allow-clear
                  @press-enter="handleQuery"
                />
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24">
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="handleQuery">
                  <a-icon type="search" />查询
                </a-button>
                <a-button style="margin-left: 8px" @click="resetQuery">
                  <a-icon type="redo" />重置
                </a-button>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>

      <div class="table-operations">
        <a-button type="primary" @click="handleExport" v-hasPermi="['insuranceOrder:insuranceOrder:export']">
          <a-icon type="download" />导出
        </a-button>
        <table-setting
          :style="{ float: 'right' }"
          :table-size.sync="tableSize"
          v-model="columns"
          :refresh-loading="loading"
          :pagination="false"
          @refresh="getList"
        />
      </div>

      <a-table
        :loading="loading"
        :data-source="tableData"
        :columns="columns"
        :size="tableSize"
        row-key="id"
        :pagination="false"
        :bordered="tableBordered"
        :row-class-name="financeRowClassName"
        :scroll="{ x: 2720 }"
      >
        <template slot="money" slot-scope="text">
          <span v-if="text != null">￥{{ Number(text).toFixed(2) }}</span>
          <span v-else>—</span>
        </template>
        <template slot="status" slot-scope="text">
          <a-tag :color="bondPayStatusColorMap[text] || 'default'">
            {{ bondPayStatusMap[text] || '—' }}
          </a-tag>
        </template>
        <template slot="source" slot-scope="text">
          {{ sourceMap[text] || '—' }}
        </template>
        <template slot="underwritingStatus" slot-scope="text">
          <a-tag :color="underwritingStatusColorMap[text] || 'default'">
            {{ underwritingStatusMap[text] || '—' }}
          </a-tag>
        </template>
        <template slot="financeHighlight" slot-scope="text, record">
          <a-tag v-if="record.financeHighlighting" color="orange">新师傅待投保</a-tag>
          <span v-else>—</span>
        </template>
        <template slot="submitDeadline" slot-scope="text">
          {{ formatDateTime(text) }}
        </template>
        <template slot="lastSubmitTime" slot-scope="text">
          {{ formatDateTime(text) }}
        </template>
        <template slot="bondPayDate" slot-scope="text">
          {{ formatDateTime(text) }}
        </template>
        <template slot="policyFileName" slot-scope="text, record">
          <span v-if="hasPolicyList(record)">{{ policyProgressText(record) }}</span>
          <a v-else-if="record.policyFileUrl" @click="viewPolicy(record)">{{ text || '查看保单' }}</a>
          <span v-else>—</span>
        </template>
        <template slot="action" slot-scope="text, record">
          <a @click="openDetail(record)" v-hasPermi="['insuranceOrder:insuranceOrder:query']">查看详情</a>
          <a-divider type="vertical" />
          <a
            v-if="canProcess(record) && !isBaige"
            @click="openStatusModal(record)"
            v-hasPermi="['insuranceOrder:insuranceOrder:edit']"
          >
            更新状态
          </a>
          <a-divider v-if="canProcess(record) && !isBaige" type="vertical" />
          <a
            v-if="canProcess(record) && !isBaige"
            @click="openUploadModal(record)"
            v-hasPermi="['insuranceOrder:insuranceOrder:edit']"
          >
            回传保单
          </a>
          <!-- 批量投保下单入口已临时隐藏：
               白鸽方案B按师傅拆单，一次下单产生 N 个独立收银台与 N 个独立 30 分钟有效期，
               旧入口只自动打开第 1 个，其余需进详情逐个支付，财务易漏付并静默过期。
               待白鸽确认「多被保人合并支付」能力或行级逐个下单方案落地后再恢复。 -->
          <!--
          <a-divider v-if="canStartInsureOrder(record)" type="vertical" />
          <a
            v-if="canStartInsureOrder(record)"
            @click="handleStartInsureOrder(record)"
            v-hasPermi="['insuranceOrder:insuranceOrder:edit']"
          >
            投保下单
          </a>
          -->
        </template>
      </a-table>

      <a-pagination
        class="ant-table-pagination"
        show-size-changer
        show-quick-jumper
        :current="queryParams.pageNum"
        :total="total"
        :page-size="queryParams.pageSize"
        :show-total="value => `共 ${value} 条`"
        @showSizeChange="onShowSizeChange"
        @change="changeSize"
      />
    </a-card>

    <a-modal
      title="保险订单详情"
      :visible="detailVisible"
      width="1200px"
      :footer="null"
      :body-style="{ maxHeight: '72vh', overflowY: 'auto', paddingTop: '8px' }"
      wrap-class-name="insurance-order-detail-modal"
      @cancel="closeDetail"
    >
      <a-spin :spinning="detailLoading">
        <div class="insurance-detail-body">
          <section class="insurance-detail-section">
            <div class="insurance-detail-section__title">基本信息</div>
            <a-descriptions
              bordered
              size="small"
              :column="2"
              class="insurance-detail-desc"
              :label-style="detailDescLabelStyle"
            >
              <a-descriptions-item label="保险订单ID">{{ detailForm.id || '—' }}</a-descriptions-item>
              <a-descriptions-item label="订单号">{{ detailForm.orderNo || '—' }}</a-descriptions-item>
              <a-descriptions-item label="保险方案">{{ detailForm.insuranceSchemeName || detailForm.name || '—' }}</a-descriptions-item>
              <a-descriptions-item label="投保工种">{{ detailForm.categoryName || '—' }}</a-descriptions-item>
              <a-descriptions-item label="支付状态">{{ bondPayStatusMap[detailForm.bondPayStatus] || '—' }}</a-descriptions-item>
              <a-descriptions-item label="投保状态">{{ underwritingStatusMap[detailForm.underwritingStatus] || '—' }}</a-descriptions-item>
              <a-descriptions-item label="投保人姓名">{{ detailForm.applicantName || '—' }}</a-descriptions-item>
              <a-descriptions-item label="投保人身份证">{{ detailForm.applicantIdCard || '—' }}</a-descriptions-item>
              <a-descriptions-item label="被保人数">{{ detailForm.insuredCount != null ? detailForm.insuredCount : '—' }}</a-descriptions-item>
              <a-descriptions-item label="支付时间">{{ formatDateTime(detailForm.bondPayDate) }}</a-descriptions-item>
              <a-descriptions-item label="最晚提交时间">{{ formatDateTime(detailForm.submitDeadline) }}</a-descriptions-item>
              <a-descriptions-item label="最新提交时间">{{ formatDateTime(detailForm.lastSubmitTime) }}</a-descriptions-item>
              <a-descriptions-item v-if="!hasPolicyList(detailForm)" label="保单上传时间">
                {{ formatDateTime(detailForm.policyUploadTime) }}
              </a-descriptions-item>
            </a-descriptions>
          </section>

          <section class="insurance-detail-section">
            <div class="insurance-detail-section__title">被保人信息</div>
            <div v-if="detailInsuredPersonList.length > 0" class="detail-insured-list">
              <div
                v-for="(row, idx) in detailInsuredPersonList"
                :key="row.tableKey"
                class="detail-insured-item"
              >
                <div class="detail-insured-item__head">被保人 {{ idx + 1 }}</div>
                <div class="detail-insured-item__row">
                  <span class="detail-insured-label">姓名</span>
                  <span class="detail-insured-value">{{ row.name }}</span>
                </div>
                <div class="detail-insured-item__row">
                  <span class="detail-insured-label">身份证</span>
                  <span class="detail-insured-value">{{ row.idCard }}</span>
                </div>
                <div class="detail-insured-item__row">
                  <span class="detail-insured-label">性别</span>
                  <span class="detail-insured-value">{{ formatDetailInsuredSex(row.sex) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="insurance-detail-empty">—</div>
          </section>

          <section class="insurance-detail-section">
            <div class="insurance-detail-section__title">保单信息</div>
            <template v-if="hasPolicyList(detailForm)">
              <div class="policy-summary">{{ policyProgressText(detailForm) }}</div>
              <a-table
                class="policy-table"
                :columns="detailPolicyColumns"
                :data-source="policyListRows(detailForm)"
                :pagination="false"
                size="small"
                row-key="id"
              >
                <template slot="detailPolicyInsured" slot-scope="text, record">
                  <div>{{ record.insuredName || '—' }}</div>
                  <div class="policy-card-no">{{ record.insuredIdCard || '—' }}</div>
                </template>
                <template slot="detailPolicyStatus" slot-scope="text, record">
                  <a-tag v-if="record.underwritingStatus === 1500" color="default">已取消</a-tag>
                  <template v-else>
                    <a-tag :color="record.policyFileUrl ? 'green' : 'orange'">
                      {{ record.policyFileUrl ? '已上传' : '未上传' }}
                    </a-tag>
                  </template>
                  <div v-if="record.policyUploadTime" class="policy-upload-time">
                    {{ formatDateTime(record.policyUploadTime) }}
                  </div>
                </template>
                <template slot="detailPolicyUnderwriting" slot-scope="text, record">
                  <a-tag :color="policyUnderwritingStatusColor(record.underwritingStatus)">
                    {{ policyUnderwritingStatusText(record.underwritingStatus) }}
                  </a-tag>
                  <div v-if="record.lastFailReason" class="policy-upload-time" :title="record.lastFailReason">
                    {{ record.lastFailReason }}
                  </div>
                </template>
                <template slot="detailPolicyPayStatus" slot-scope="text, record">
                  <a-tag :color="policyInsurerPayStatusColor(record.insurerPayStatus)">
                    {{ policyInsurerPayStatusText(record.insurerPayStatus) }}
                  </a-tag>
                </template>
                <template slot="detailPolicyAction" slot-scope="text, record">
                  <!-- 行级投保下单（按师傅逐个下单，成功即打开收银台支付，收银台 30 分钟过期）：
                       未下单/下单失败 → 投保下单；已下单未过期 → 继续支付（复用原收银台，不重复投保）；
                       已下单已过期 → 重新生成收银台（后端换 subSerialNo 重新投保下单） -->
                  <a v-if="policyActionType(record) === 'create'" @click="handlePolicyInsureOrder(record)">投保下单</a>
                  <a v-else-if="policyActionType(record) === 'renew'" @click="handlePolicyInsureOrder(record)">重新生成收银台</a>
                  <a v-else-if="policyActionType(record) === 'pay'" @click="handlePolicyContinuePay(record)">
                    继续支付<em v-if="policyCashierRemainText(record)" class="policy-cashier-remain">（{{ policyCashierRemainText(record) }}）</em>
                  </a>
                  <template v-if="isPolicyNeedCheck(record)">
                    <a-divider v-if="policyActionType(record) !== 'none'" type="vertical" />
                    <!-- 待核对只能由人工核销放出：系统已无法确认白鸽侧是否扣款 -->
                    <a @click="handleManualVerify(record)">人工核销</a>
                  </template>
                  <a-divider
                    v-if="(policyActionType(record) !== 'none' || isPolicyNeedCheck(record)) && record.policyFileUrl"
                    type="vertical"
                  />
                  <a v-if="record.policyFileUrl" @click="viewPolicy(record)">查看</a>
                  <!-- 台账是查证「这笔钱到底有没有付白鸽」的关键入口，任何明细行都应该能看 -->
                  <a-divider
                    v-if="policyActionType(record) !== 'none' || isPolicyNeedCheck(record) || record.policyFileUrl"
                    type="vertical"
                  />
                  <!-- 台账可见单独立权限点控制：导出内容含身份证号，必须收口 -->
                  <a v-hasPermi="['insuranceOrder:insuranceOrder:payRecord']" @click="openPayRecordDrawer(record)">台账</a>
                </template>
              </a-table>
            </template>
            <template v-else>
              <a v-if="detailForm.policyFileUrl" @click="viewPolicy(detailForm)">
                {{ detailForm.policyFileName || '查看保单' }}
              </a>
              <span v-else class="insurance-detail-empty">—</span>
            </template>
            <div v-if="detailForm.policyFileUrl && !hasPolicyList(detailForm)" class="detail-policy-action">
              <a-button type="primary" size="small" @click="viewPolicy(detailForm)">查看保单</a-button>
            </div>
          </section>

          <section class="insurance-detail-section">
            <div class="insurance-detail-section__title">处理信息</div>
            <a-descriptions
              bordered
              size="small"
              :column="1"
              class="insurance-detail-desc"
              :label-style="detailDescLabelStyle"
            >
              <a-descriptions-item label="失败原因">{{ detailForm.lastFailReason || '—' }}</a-descriptions-item>
              <a-descriptions-item label="人工备注">{{ detailForm.manualRemark || '—' }}</a-descriptions-item>
            </a-descriptions>
          </section>

          <section class="insurance-detail-section">
            <div class="insurance-detail-section__title">数据快照</div>
            <div class="insurance-detail-snapshot">
              <div class="insurance-detail-snapshot__label">投保人快照</div>
              <a-textarea :value="detailForm.applicantSnapshot || ''" :auto-size="{ minRows: 3, maxRows: 6 }" read-only />
            </div>
            <div class="insurance-detail-snapshot">
              <div class="insurance-detail-snapshot__label">被保人快照</div>
              <a-textarea :value="detailForm.insuredSnapshot || ''" :auto-size="{ minRows: 3, maxRows: 6 }" read-only />
            </div>
          </section>
        </div>
      </a-spin>
    </a-modal>

    <a-modal
      title="回传保单"
      :visible="uploadVisible"
      :confirm-loading="uploadSubmitting"
      @ok="submitUploadPolicy"
      @cancel="closeUploadModal"
    >
      <a-form-model :model="uploadForm" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-model-item label="保险订单">
          <a-input :value="uploadForm.id" disabled />
        </a-form-model-item>
        <a-form-model-item label="被保人" v-if="uploadPolicyList.length > 0">
          <a-select
            v-model="uploadForm.policyId"
            placeholder="请选择被保人"
            @change="handleUploadPolicyChange"
          >
            <a-select-option v-for="item in uploadPolicyList" :key="item.id" :value="item.id">
              {{ formatPolicyOption(item) }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="上传 PDF">
          <file-upload
            :key="uploadForm.policyId || uploadForm.id || 'policy-upload'"
            type="file"
            :count="1"
            :defaultList="uploadFileList"
            :max-size="10"
            accept=".pdf,application/pdf"
            @input="handlePolicyFileChange"
          />
          <div class="upload-hint">请上传 PDF 保单，大小不超过 10MB</div>
        </a-form-model-item>
        <a-form-model-item label="处理备注">
          <a-textarea v-model="uploadForm.manualRemark" :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <a-modal
      title="更新投保状态"
      :visible="statusVisible"
      :confirm-loading="statusSubmitting"
      @ok="submitManualStatus"
      @cancel="closeStatusModal"
    >
      <a-form-model :model="statusForm" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-model-item label="保险订单">
          <a-input :value="statusForm.id" disabled />
        </a-form-model-item>
        <a-form-model-item label="投保状态">
          <a-select v-model="statusForm.underwritingStatus" placeholder="请选择投保状态">
            <a-select-option :value="1000">待投保</a-select-option>
            <a-select-option :value="1100">投保中</a-select-option>
            <a-select-option :value="1400">投保失败</a-select-option>
            <a-select-option :value="1500">已取消</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="失败原因" v-if="statusForm.underwritingStatus === 1400">
          <a-textarea v-model="statusForm.lastFailReason" :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-model-item>
        <a-form-model-item label="处理备注">
          <a-textarea v-model="statusForm.manualRemark" :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <a-modal
      title="白鸽保单人工核销"
      :visible="verifyVisible"
      :confirm-loading="verifySubmitting"
      ok-text="提交核销"
      @ok="submitManualVerify"
      @cancel="verifyVisible = false"
    >
      <a-alert
        type="warning"
        show-icon
        style="margin-bottom: 16px"
        message="请先到白鸽后台核对这笔是否真的扣款，再选择结论。"
        description="选择「已支付并出单」会把该师傅保单置为投保成功/已付款，并清空收银台；选择「确认未支付」会置为已取消。核销结果将写入付款台账备查。"
      />
      <a-form-model :model="verifyForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="核销结论">
          <a-radio-group v-model="verifyForm.conclusion">
            <a-radio :value="1">白鸽已扣款并出单</a-radio>
            <a-radio :value="0">确认白鸽未扣款</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item label="保单号" v-if="verifyForm.conclusion === 1">
          <a-input v-model="verifyForm.policyNo" placeholder="请填写白鸽后台的保单号" />
        </a-form-model-item>
        <a-form-model-item label="电子保单" v-if="verifyForm.conclusion === 1">
          <a-input v-model="verifyForm.policyFileUrl" placeholder="可填写电子保单地址（选填）" />
        </a-form-model-item>
        <a-form-model-item label="核销依据">
          <a-textarea
            v-model="verifyForm.remark"
            :auto-size="{ minRows: 3, maxRows: 6 }"
            placeholder="建议填写白鸽后台核对到的支付流水号，便于日后追溯"
          />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <a-drawer
      title="白鸽保单付款台账"
      placement="right"
      :width="1180"
      :visible="payRecordVisible"
      @close="payRecordVisible = false"
    >
      <div style="margin-bottom: 12px">
        <a-button type="primary" icon="download" @click="exportPayRecord">导出 Excel</a-button>
        <a-button icon="reload" style="margin-left: 8px" @click="fetchPayRecord">刷新</a-button>
        <span style="margin-left: 12px; color: #888">
          共 {{ payRecordList.length }} 条<template v-if="payRecordInsuredName">，被保人：{{ payRecordInsuredName }}</template>
        </span>
      </div>
      <a-table
        :columns="payRecordColumns"
        :data-source="payRecordList"
        :loading="payRecordLoading"
        :row-key="record => record.id"
        :pagination="false"
        :scroll="{ x: 1600 }"
        size="small"
        bordered
      />
      <a-alert
        v-if="!payRecordLoading && payRecordList.length === 0"
        style="margin-top: 16px"
        type="info"
        show-icon
        message="该师傅暂无付款台账记录"
        description="台账只记录「已经发生或可能已经流转到白鸽侧」的关键事件。若这里为空，说明这笔尚未向白鸽下单或白鸽通道未启用。"
      />
    </a-drawer>
  </page-header-wrapper>
</template>

<script>
import { parseTime } from '@/utils/ruoyi'
import { tableMixin } from '@/store/table-mixin'
import * as insuranceOrderApi from '@/api/insuranceOrder/insuranceOrder'
import { saveAs } from 'file-saver'

const bondPayStatusMap = {
  1: '待支付',
  2: '已支付',
  3: '已取消'
}

const bondPayStatusColorMap = {
  1: 'orange',
  2: 'green',
  3: 'red'
}

const sourceMap = {
  0: '未知',
  10: '雇主订单',
  20: '师傅端购买',
  30: '企业购买'
}

const underwritingStatusMap = {
  0: '未触发投保',
  1000: '待投保',
  1100: '投保中',
  1200: '投保成功',
  1250: '部分成功',
  1300: '投保失败待处理',
  1400: '投保失败',
  1500: '已取消',
  1600: '待核对'
}

const underwritingStatusColorMap = {
  0: 'blue',
  1000: 'cyan',
  1100: 'blue',
  1200: 'green',
  1250: 'gold',
  1300: 'orange',
  1400: 'red',
  1500: 'red'
}

export default {
  name: 'InsuranceOrder',
  mixins: [tableMixin],
  data() {
    return {
      bondPayStatusMap,
      bondPayStatusColorMap,
      sourceMap,
      underwritingStatusMap,
      underwritingStatusColorMap,
      underwritingStatusOptions: Object.keys(underwritingStatusMap).map(key => ({
        value: Number(key),
        label: underwritingStatusMap[key]
      })),
      loading: false,
      tableData: [],
      total: 0,
      detailVisible: false,
      detailLoading: false,
      detailForm: {},
      // 详情弹窗内对明细行付款状态的轮询（点击收银台后启动）
      detailPollTimer: null,
      detailPollInterval: 3000,
      // 详情轮询单次最长时长（毫秒）：防止异常下单导致无限轮询
      detailPollMaxAge: 10 * 60 * 1000,
      // 列表页独立轮询：跟详情弹窗无关，投保下单或点击「继续支付」后启动；
      // 用于用户在等支付时关闭/离开弹窗后，列表仍能自动更新
      listPollTimer: null,
      listPollInterval: 5000,
      listPollMaxAge: 5 * 60 * 1000,
      // 待跟踪的明细行：{orderId, policyId, startedAt}；达到终态后从列表中移除并停止轮询
      pendingPolicies: [],
      uploadVisible: false,
      uploadSubmitting: false,
      uploadForm: {
        id: null,
        policyId: null,
        policyFileUrl: '',
        policyFileName: '',
        manualRemark: ''
      },
      uploadFileList: [],
      uploadPolicyList: [],
      verifyVisible: false,
      verifySubmitting: false,
      verifyForm: {
        insuranceOrderId: null,
        policyId: null,
        conclusion: 1,
        policyNo: '',
        policyFileUrl: '',
        remark: ''
      },
      // 付款台账抽屉：展示某位师傅这笔钱经历的全部事件（下单→回调→查询→核销）
      payRecordVisible: false,
      payRecordLoading: false,
      payRecordList: [],
      payRecordPolicyId: null,
      payRecordInsuredName: '',
      payRecordColumns: [
        { title: '发生时间', dataIndex: 'createTime', width: 160 },
        { title: '事件类型', dataIndex: 'eventTypeName', width: 100 },
        { title: '触发来源', dataIndex: 'eventSource', width: 90 },
        { title: '扣款结论', dataIndex: 'payResultName', width: 90 },
        { title: '对账状态', dataIndex: 'reconcileStatusName', width: 120 },
        { title: '我方保费', dataIndex: 'premiumAmount', width: 90 },
        { title: '白鸽实收', dataIndex: 'baigePremium', width: 90 },
        { title: '金额一致', dataIndex: 'amountMatchName', width: 90 },
        { title: '白鸽状态码', dataIndex: 'baigePolicyStatus', width: 100 },
        { title: '子流水号', dataIndex: 'subSerialNo', width: 160 },
        { title: '白鸽订单号', dataIndex: 'baigeOrderNo', width: 160 },
        { title: '备注', dataIndex: 'remark', ellipsis: true }
      ],
      statusVisible: false,
      statusSubmitting: false,
      statusForm: {
        id: null,
        underwritingStatus: 1100,
        lastFailReason: '',
        manualRemark: ''
      },
      queryParams: {
        pageNum: 1,
        pageSize: 10,
        name: '',
        customerOrderId: undefined,
        bondPayStatus: undefined,
        underwritingStatus: undefined
      },
      columns: [
        { title: '待投保提醒', dataIndex: 'financeHighlighting', width: 110, align: 'center', scopedSlots: { customRender: 'financeHighlight' } },
        { title: '保险订单ID', dataIndex: 'id', width: 170, ellipsis: true },
        { title: '订单号', dataIndex: 'orderNo', width: 160, ellipsis: true },
        { title: '保险计划分类', dataIndex: 'planName', width: 120, ellipsis: true },
        { title: '保险分类', dataIndex: 'insuranceTypeName', width: 100, ellipsis: true },
        { title: '保险方案名称', dataIndex: 'insuranceSchemeName', width: 160, ellipsis: true },
        { title: '投保工种', dataIndex: 'categoryName', width: 140, ellipsis: true },
        { title: '投保人姓名', dataIndex: 'applicantName', width: 120, ellipsis: true },
        { title: '投保人身份证号', dataIndex: 'applicantIdCard', width: 180, ellipsis: true },
        { title: '金额', dataIndex: 'money', width: 100, scopedSlots: { customRender: 'money' } },
        { title: '支付状态', dataIndex: 'bondPayStatus', width: 100, scopedSlots: { customRender: 'status' } },
        { title: '投保来源', dataIndex: 'source', width: 110, scopedSlots: { customRender: 'source' } },
        { title: '投保状态', dataIndex: 'underwritingStatus', width: 120, scopedSlots: { customRender: 'underwritingStatus' } },
        { title: '被保人数', dataIndex: 'insuredCount', width: 90, align: 'center' },
        { title: '最晚提交时间', dataIndex: 'submitDeadline', width: 160, scopedSlots: { customRender: 'submitDeadline' } },
        { title: '最新提交时间', dataIndex: 'lastSubmitTime', width: 160, scopedSlots: { customRender: 'lastSubmitTime' } },
        { title: '支付时间', dataIndex: 'bondPayDate', width: 160, scopedSlots: { customRender: 'bondPayDate' } },
        { title: '保单', dataIndex: 'policyFileName', width: 160, scopedSlots: { customRender: 'policyFileName' } },
        { title: '操作', dataIndex: 'action', width: 280, fixed: 'right', scopedSlots: { customRender: 'action' } }
      ],
      detailPolicyColumns: [
        { title: '被保人', dataIndex: 'insuredName', width: 180, scopedSlots: { customRender: 'detailPolicyInsured' } },
        { title: '子流水号', dataIndex: 'subSerialNo', width: 130 },
        { title: '白鸽订单号', dataIndex: 'baigeOrderNo', width: 140 },
        { title: '保单号', dataIndex: 'thirdPolicyNo', width: 140 },
        { title: '投保状态', dataIndex: 'underwritingStatus', width: 110, scopedSlots: { customRender: 'detailPolicyUnderwriting' } },
        { title: '付款状态', dataIndex: 'insurerPayStatus', width: 100, scopedSlots: { customRender: 'detailPolicyPayStatus' } },
        { title: '保费(元)', dataIndex: 'premiumAmount', width: 90, align: 'right' },
        { title: '保单文件', dataIndex: 'policyFileUrl', width: 130, scopedSlots: { customRender: 'detailPolicyStatus' } },
        { title: '操作', dataIndex: 'action', width: 180, scopedSlots: { customRender: 'detailPolicyAction' } }
      ],
      detailDescLabelStyle: {
        width: '112px',
        minWidth: '112px',
        whiteSpace: 'normal',
        wordBreak: 'keep-all',
        verticalAlign: 'top',
        lineHeight: '1.5'
      }
    }
  },
  computed: {
    // baige 角色（财务账号）：不允许更新状态、回传保单
    // 注意：必须绕开 auth.hasRole —— auth.js 把 admin 当作超管通配，
    // 只要用户拥有 admin 角色，hasRole('baige') 会恒为 true，会误把 admin 也当成 baige。
    isBaige() {
      const roles = (this.$store.getters && this.$store.getters.roles) || []
      return Array.isArray(roles) && roles.indexOf('baige') !== -1
    },
    detailInsuredPersonList() {
      const form = this.detailForm || {}
      const fromSnap = this.parseInsuredSnapshotArray(form.insuredSnapshot)
      if (fromSnap.length > 0) {
        return fromSnap.map((item, index) => ({
          name: item.name || item.pname || '—',
          idCard: item.idCard || item.cardNumber || item.cardno || '—',
          sex: item.sex,
          tableKey: `${item.id != null ? item.id : index}-${item.idCard || item.cardNumber || item.name || ''}`
        }))
      }
      const splitParts = (s) =>
        String(s || '')
          .split(/\r?\n|、|;/)
          .map((t) => t.trim())
          .filter((t) => t)
      const names = splitParts(form.insuredNames)
      const ids = splitParts(form.insuredIdCards)
      const n = Math.max(names.length, ids.length)
      if (n === 0) {
        return []
      }
      const rows = []
      for (let i = 0; i < n; i++) {
        rows.push({
          name: names[i] || '—',
          idCard: ids[i] || '—',
          sex: undefined,
          tableKey: `fallback-${i}-${ids[i] || names[i] || ''}`
        })
      }
      return rows
    }
  },
  created() {
    this.getList()
    // 来自消息通知跳转（/pay/insuranceOrder?id=xx&fromNotice=true）：自动打开对应保险订单详情
    this.openFromNoticeIfNeeded(this.$route.query)
  },
  watch: {
    // 同一路由内仅 query 变化（如代办"去处理"重复点击）时不会重跑 created，补监听
    '$route.query': {
      handler (q) {
        this.openFromNoticeIfNeeded(q)
      }
    }
  },
  beforeDestroy() {
    // 防止离开页面/关闭弹窗后定时器泄漏
    this.stopDetailPoll()
    this.stopListPoll()
  },
  methods: {
    parseTime,
    /** 财务高亮行：存在"已抢单但未投保下单"的师傅且订单未开工（后端动态计算） */
    financeRowClassName(record) {
      return record && record.financeHighlighting ? 'finance-highlight-row' : ''
    },
    formatDateTime(value) {
      return value ? parseTime(value, '{y}-{m}-{d} {h}:{i}:{s}') : '—'
    },
    formatDetailInsuredSex(sex) {
      if (sex === '男' || sex === '女') return sex
      if (Number(sex) === 2) return '女'
      if (sex != null && sex !== '') return '男'
      return '—'
    },
    parseInsuredSnapshotArray(value) {
      if (!value || typeof value !== 'string') return []
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    },
    getList() {
      this.loading = true
      insuranceOrderApi.pageInsurance(this.queryParams)
        .then(res => {
          if (res && res.success) {
            this.tableData = (res.data && res.data.records) || []
            this.total = (res.data && res.data.total) || 0
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleQuery() {
      this.queryParams.pageNum = 1
      this.getList()
    },
    resetQuery() {
      this.queryParams = {
        pageNum: 1,
        pageSize: this.queryParams.pageSize,
        name: '',
        customerOrderId: undefined,
        bondPayStatus: undefined,
        underwritingStatus: undefined
      }
      this.getList()
    },
    onShowSizeChange(current, pageSize) {
      this.queryParams.pageSize = pageSize
      this.queryParams.pageNum = 1
      this.getList()
    },
    changeSize(current, pageSize) {
      this.queryParams.pageNum = current
      this.queryParams.pageSize = pageSize
      this.getList()
    },
    /**
     * 是否可发起白鸽投保下单：白鸽通道、尚未出单、且平台未付款
     * @param {Object} record 保险订单
     */
    canStartInsureOrder(record) {
      if (!record) return false
      if (record.channel !== 'baige') return false
      // 拆单：明细行全部已发起过白鸽下单（有白鸽订单号）即隐藏；
      // 任一明细还没有白鸽订单号（未下单/下单失败待重发）则显示，与后端 payInsureOrderBatch 幂等口径一致
      const policies = Array.isArray(record.policyList) ? record.policyList : []
      if (policies.length > 0) {
        if (policies.every(p => p.baigeOrderNo)) return false
      } else {
        // 兼容：明细行还没生成（极少见），按主单状态兜底
        if (Number(record.underwritingStatus) === 1200) return false
      }
      // 已付款不可重发（避免重复扣款）
      // insurerPayStatus：0-待付款 1-付款中 2-已付款 3-已退款
      return Number(record.insurerPayStatus) !== 2
    },
    /** 投保下单：向保司发起投保并打开收银台完成保费支付 */
    handleStartInsureOrder(record) {
      const that = this
      this.$confirm({
        title: '投保下单',
        content: `确认对保险订单 ${record.id} 发起保司投保下单？成功后请在弹出的收银台完成保费支付。`,
        onOk() {
          return insuranceOrderApi.startInsureOrder({ id: record.id }).then(res => {
            if (!res || !res.success) {
              that.$message.error((res && res.message) || '投保下单失败')
              return
            }
            // 白鸽拆单：每个师傅一个收银台，Admin 逐个完成保费支付
            const data = res.data || {}
            const cashierUrls = Array.isArray(data.cashierUrlList) && data.cashierUrlList.length > 0
              ? data.cashierUrlList.filter(url => url)
              : (data.cashierUrl ? [data.cashierUrl] : [])
            // 逐师傅处理结果：部分成功/部分失败是常态，必须明确提示失败的人与原因，
            // 否则只弹出「投保下单成功」会让财务漏掉未投保的师傅。
            const outcomeList = Array.isArray(data.policyOutcomeList) ? data.policyOutcomeList : []
            const failList = outcomeList.filter(item => !item.success)
            that.getList()
            if (failList.length > 0) {
              const successCount = outcomeList.length - failList.length
              that.$warning({
                title: `投保下单完成：成功 ${successCount} 人，失败 ${failList.length} 人`,
                content: that.$createElement('div', failList.map(item =>
                  that.$createElement('div', { style: 'margin-bottom: 4px;' },
                    `${item.insuredName || '—'}：${item.message || '下单失败'}`)
                )),
                okText: '知道了'
              })
            } else {
              that.$message.success('投保下单成功')
            }
            if (cashierUrls.length === 1) {
              window.open(cashierUrls[0], '_blank')
            } else if (cashierUrls.length > 1) {
              that.$message.info(`共 ${cashierUrls.length} 个收银台，已打开第 1 个，其余请在「详情 - 保单信息」中逐个支付`)
              window.open(cashierUrls[0], '_blank')
            } else if (failList.length === 0) {
              that.$message.warning('未返回收银台地址，请稍后在详情中确认支付状态')
            }
            // 多收银台需逐个支付、或有师傅失败时，直接打开详情便于继续处理
            if (cashierUrls.length > 1 || failList.length > 0) {
              that.openDetail(record)
            }
          })
        }
      })
    },

    handleExport() {
      this.download(
        'insuranceOrder/insuranceOrder/export',
        { ...this.queryParams },
        `保险订单_${new Date().getTime()}.xls`
      )
    },
    canProcess(record) {
      return record && record.bondPayStatus === 2
    },
    hasPolicyList(record) {
      return Array.isArray(record && record.policyList) && record.policyList.length > 0
    },
    policyListRows(record) {
      return this.hasPolicyList(record) ? record.policyList : []
    },
    policyProgressText(record) {
      const total = this.policyTotalCount(record)
      const uploaded = this.policyUploadedCount(record)
      return total > 0 ? `已上传 ${uploaded}/${total}` : '—'
    },
    policyTotalCount(record) {
      if (record && record.policyTotalCount != null) {
        return Number(record.policyTotalCount) || 0
      }
      return this.hasPolicyList(record) ? record.policyList.length : 0
    },
    policyUploadedCount(record) {
      if (record && record.policyUploadedCount != null) {
        return Number(record.policyUploadedCount) || 0
      }
      if (!this.hasPolicyList(record)) {
        return record && record.policyFileUrl ? 1 : 0
      }
      return record.policyList.filter(item => item && item.policyFileUrl).length
    },
    /**
     * 处理"代办→去处理"跳转：query 里带 fromNotice=true&id=xxx 时自动打开保险订单详情
     * - created 与 $route.query watch 共用，避免同路由 query 变化时 created 不重跑
     */
    openFromNoticeIfNeeded (q) {
      const query = q || (this.$route && this.$route.query) || {}
      const { id, fromNotice } = query
      if (fromNotice && id) {
        // 注意：id 是字符串（来自 URL query），不能 Number() 转换——snowflake ID 超出 2^53 会丢精度
        this.openDetail({ id })
      }
    },
    openDetail(record) {
      this.detailVisible = true
      this.detailLoading = true
      insuranceOrderApi.getUnderwritingDetail({ id: record.id })
        .then(res => {
          if (res && res.success) {
            this.detailForm = res.data || {}
          } else {
            this.detailForm = {}
            this.$message.error((res && res.message) || '加载保险订单详情失败')
          }
        })
        .catch(err => {
          // 业务"不存在"类异常（保险单已被取消/删除）：自动关弹窗、清掉来源 URL，避免用户卡在 fromNotice 试错循环
          this.detailForm = {}
          this.detailVisible = false
          const msg = (err && err.message) || '加载保险订单详情失败'
          this.$message.error(msg)
          this.clearFromNoticeQuery()
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    /**
     * 清掉地址栏上 fromNotice / id query，避免"代办→去处理"路径下目标失效后一直重复报同样错
     */
    clearFromNoticeQuery () {
      const q = (this.$route && this.$route.query) || {}
      if (!q.fromNotice && q.id == null) return
      const next = { ...q }
      delete next.fromNotice
      delete next.id
      this.$router.replace({ path: this.$route.path, query: next }).catch(() => {})
    },
    closeDetail() {
      this.stopDetailPoll()
      this.detailVisible = false
      this.detailForm = {}
    },
    /**
     * 明细行操作类型：
     * - create：未下单 / 下单失败 → 投保下单
     * - pay：已下单且收银台未过期 → 继续支付（复用原收银台，不重复投保）
     * - renew：已下单但收银台已过期 → 重新生成收银台（后端换 subSerialNo 重新投保下单）
     * - none：已付款 / 已退款 / 已出单 → 不再提供下单与支付入口
     */
    policyActionType(record) {
      if (!record) return 'none'
      // 两个维度的阻断，服务端都会断言拒绝，入口一并隐藏避免点了才报错：
      // 1) orderInServiceOrLater：雇主订单进入服务中及之后 —— 整张单都不允许再投保（最强条件）
      // 2) masterOrderStarted：该师傅自己已开工 —— 保险须覆盖服务全程，事后补单拿不到有效保障
      if (record.orderInServiceOrLater || record.masterOrderStarted) return 'none'
      // 已取消（1500：师傅中途取消 / 招工订单取消）：不再提供投保下单与继续支付入口
      // 待核对（1600：已在白鸽下单但结果未知，可能已扣款）：
      //   既不能重新下单（重复扣款风险），也不能继续支付，须先到白鸽后台核实后人工核销
      const undStatus = Number(record.underwritingStatus)
      if (undStatus === 1500 || undStatus === 1600) return 'none'
      const payStatus = Number(record.insurerPayStatus)
      if (payStatus === 2 || payStatus === 3) return 'none'
      if (record.thirdPolicyNo) return 'none'
      if (!record.baigeOrderNo || !record.cashierUrl) return 'create'
      // 付款中(1)：财务可能正在支付，不主动作废收银台，仅引导继续支付
      if (payStatus === 1) return 'pay'
      return record.cashierExpired ? 'renew' : 'pay'
    },
    /** 该明细是否处于「待核对」：系统已无法确认白鸽侧是否扣款，只能人工核销 */
    isPolicyNeedCheck(record) {
      return !!record && Number(record.underwritingStatus) === 1600
    },
    /** 打开人工核销弹窗 */
    handleManualVerify(record) {
      this.verifyForm = {
        insuranceOrderId: record.insuranceOrderId,
        policyId: record.id,
        conclusion: 1,
        policyNo: record.thirdPolicyNo || '',
        policyFileUrl: '',
        remark: ''
      }
      this.verifyVisible = true
    },
    /** 提交人工核销结论 */
    submitManualVerify() {
      if (this.verifyForm.conclusion === 1 && !this.verifyForm.policyNo) {
        this.$message.warning('核销为「已支付并出单」时，保单号必填')
        return
      }
      const currentId = this.verifyForm.insuranceOrderId
      this.verifySubmitting = true
      insuranceOrderApi.manualVerifyPolicyPay(this.verifyForm)
        .then(() => {
          this.$message.success('核销成功')
          this.verifyVisible = false
          this.getList()
          if (this.detailVisible && this.detailForm.id === currentId) {
            this.openDetail({ id: currentId })
          }
        })
        .catch(e => {
          this.$message.error((e && e.message) || '核销失败')
        })
        .finally(() => {
          this.verifySubmitting = false
        })
    },
    /** 打开某位师傅的付款台账抽屉 */
    openPayRecordDrawer(record) {
      this.payRecordPolicyId = record.id
      this.payRecordInsuredName = record.insuredName || ''
      this.payRecordList = []
      this.payRecordVisible = true
      this.fetchPayRecord()
    },
    fetchPayRecord() {
      if (!this.payRecordPolicyId) return
      this.payRecordLoading = true
      // 拦截器返回的是响应体本体 CommonResponse，分页数据在其 data 字段里
      insuranceOrderApi.payRecordPage({ policyId: this.payRecordPolicyId, pageNum: 1, pageSize: 200 })
        .then(res => {
          const page = res && res.data
          this.payRecordList = (page && page.records) || []
        })
        .catch(e => {
          this.$message.error((e && e.message) || '台账加载失败')
        })
        .finally(() => {
          this.payRecordLoading = false
        })
    },
    /** 导出当前筛选条件的台账（默认为该师傅，导出为 xls） */
    exportPayRecord() {
      insuranceOrderApi.payRecordExport({ policyId: this.payRecordPolicyId })
        .then(data => {
          if (!data) {
            this.$message.warning('没有可导出的数据')
            return
          }
          const blob = new Blob([data], { type: 'application/vnd.ms-excel' })
          saveAs(blob, '白鸽保单付款台账.xls')
          this.$message.success('导出成功')
        })
        .catch(e => {
          this.$message.error((e && e.message) || '导出失败')
        })
    },
    /**
     * 「继续支付」：打开收银台前先向白鸽同步该保单的真实状态。
     * <p>
     * 白鸽订单状态靠异步回调通知我方，回调丢失后本地明细会长期停在「待付款」，
     * 而白鸽侧其实已经完成支付 —— 此时直接打开旧收银台会被白鸽提示「订单已支付」（E1 报错），
     * 财务既付不了款，也看不出这笔钱其实早就付了，一直在这里反复重试。
     * <p>
     * 同步失败（白鸽查询接口不可用）时降级按本地状态打开，不阻断正常支付。
     */
    handlePolicyContinuePay(record) {
      const that = this
      insuranceOrderApi.syncPolicyStatus({
        insuranceOrderId: record.insuranceOrderId,
        policyId: record.id
      }).then(res => {
        const latest = (res && res.data) || record
        const payStatus = Number(latest.insurerPayStatus)
        const undStatus = Number(latest.underwritingStatus)
        // 同步后已非「可支付」状态（已付款 / 已退款 / 已出单 / 已取消 / 待核对）：不再打开收银台
        if (payStatus === 2 || payStatus === 3 || latest.thirdPolicyNo
          || undStatus === 1500 || undStatus === 1600) {
          that.$message.warning('该师傅保单状态已同步为「'
            + (payStatus === 2 || latest.thirdPolicyNo ? '已支付/已出单'
              : payStatus === 3 ? '已退款'
                : undStatus === 1600 ? '待核对（可能已扣款，请到白鸽后台核实）' : '已取消')
            + '」，无需继续支付，请刷新后确认')
          that.reloadDetail(record.insuranceOrderId)
          return
        }
        that.openCashierAndPoll(latest, latest.cashierUrl || record.cashierUrl)
      }).catch(() => {
        that.openCashierAndPoll(record, record.cashierUrl)
      })
    },
    /** 收银台剩余有效期文案（由服务端返回的过期时间推算，不额外起定时器） */
    policyCashierRemainText(record) {
      const expire = record && record.cashierExpireTime
      if (!expire) return ''
      const expireTime = new Date(String(expire).replace(/-/g, '/')).getTime()
      if (!expireTime) return ''
      const diff = expireTime - Date.now()
      if (diff <= 0) return '已过期'
      const minutes = Math.ceil(diff / 60000)
      return minutes > 60 ? `剩余${Math.ceil(minutes / 60)}小时` : `剩余${minutes}分钟`
    },
    /**
     * 行级投保下单：先同步开「处理中」提示页保住用户点击手势，再请求接口回填真实收银台地址。
     * 若先 await 请求再 window.open，会被浏览器当作异步弹窗拦截。
     */
    handlePolicyInsureOrder(record) {
      const that = this
      const isRenew = this.policyActionType(record) === 'renew'
      const insuredName = (record && record.insuredName) || '该师傅'
      this.$confirm({
        title: isRenew ? '重新生成收银台' : '投保下单',
        content: isRenew
          ? `师傅「${insuredName}」的原收银台已过期，需重新投保下单生成新收银台，是否继续？`
          : `确认为师傅「${insuredName}」发起投保下单？成功后请在收银台完成保费支付（有效期 30 分钟）。`,
        onOk() {
          // 同步占位窗口：不能让用户手势丢失，否则异步回调里开窗会被拦截；
          // 用「处理中」提示页代替 about:blank，避免白屏让用户误以为页面坏了
          const payWindow = that.openCashierLoadingWindow(insuredName)
          insuranceOrderApi.startInsureOrderByPolicy({
            insuranceOrderId: record.insuranceOrderId,
            policyId: record.id
          }).then(res => {
            const data = (res && res.data) || {}
            if (!res || !res.success || !data.success) {
              that.closeCashierLoadingWindow(payWindow)
              that.$message.error((data && data.message) || (res && res.message) || '投保下单失败')
              // 失败也刷新：可能是并发下单已被其它请求抢占
              that.reloadDetail(record.insuranceOrderId)
              return
            }
            // 后端返回了业务提示时优先展示（如「白鸽侧已支付并出单，已自动补录状态」），
            // 避免出现「请完成保费支付」但实际根本没有收银台需要支付的误导文案
            that.$message.success((data && data.message)
              || (data.renewed ? '已重新生成收银台，请尽快完成支付' : '投保下单成功，请完成保费支付'))
            if (payWindow && !payWindow.closed && data.cashierUrl) {
              payWindow.location.href = data.cashierUrl
            } else if (data.cashierUrl) {
              // 提示页被用户手动关闭：当前页同步打开兜底（仍在消息回调内可能被拦截，
              // 拦截时引导走详情里的「继续支付」入口）
              const fallback = window.open(data.cashierUrl, '_blank')
              if (!fallback) {
                that.$message.warning('浏览器拦截了支付页弹窗，请在详情「保单信息」中点击「继续支付」')
              }
            } else {
              that.$message.warning('未返回收银台地址，请刷新后确认支付状态')
            }
            that.reloadDetail(record.insuranceOrderId)
            // 打开收银台后启动该行轮询，付款成功自动刷新弹窗与列表
            that.startDetailPoll(record.insuranceOrderId, record.id)
            // 同时加入列表级轮询：用户即便关掉弹窗去支付，列表也会自动刷新
            that.addPendingPolicy(record.insuranceOrderId, record.id)
          }).catch(() => {
            that.closeCashierLoadingWindow(payWindow)
            that.$message.error('投保下单失败，请稍后重试')
          })
        }
      })
    },
    /**
     * 打开「投保下单处理中」提示页（同步调用，保住用户点击手势）。
     * 返回窗口引用；若被浏览器拦截返回 null（后续走「继续支付」兜底）。
     */
    openCashierLoadingWindow(insuredName) {
      const payWindow = window.open('about:blank', '_blank')
      if (!payWindow) {
        this.$message.warning('浏览器拦截了新窗口，投保下单成功后请在详情「保单信息」中点击「继续支付」')
        return null
      }
      const name = this.escapeHtml(insuredName || '该师傅')
      const html = [
        '<!DOCTYPE html><html lang="zh-CN"><head><meta charset="utf-8">',
        '<meta name="viewport" content="width=device-width,initial-scale=1">',
        '<title>投保下单处理中</title><style>',
        '*{margin:0;padding:0;box-sizing:border-box}',
        'body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI","PingFang SC","Microsoft YaHei",sans-serif;background:#f5f6f8;color:rgba(0,0,0,.65)}',
        '.wrap{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px;text-align:center}',
        '.spinner{width:44px;height:44px;margin-bottom:20px;border:4px solid #e6e8eb;border-top-color:#1890ff;border-radius:50%;animation:spin 1s linear infinite}',
        '@keyframes spin{to{transform:rotate(360deg)}}',
        '.title{font-size:17px;font-weight:600;color:rgba(0,0,0,.85);margin-bottom:8px}',
        '.desc{font-size:13px;line-height:1.8}',
        '.desc b{color:#1890ff;font-weight:600}',
        '.warn{margin-top:14px;font-size:12px;color:#fa8c16}',
        '</style></head><body><div class="wrap">',
        '<div class="spinner"></div>',
        `<div class="title">正在向保司投保下单…</div>`,
        `<div class="desc">师傅：<b>${name}</b><br>预计 10~30 秒，完成后本页将自动打开收银台</div>`,
        '<div class="warn">请勿关闭本页，关闭后仍可在后台「详情 - 保单信息」中继续支付</div>',
        '</div></body></html>'
      ].join('')
      try {
        payWindow.document.write(html)
        payWindow.document.close()
      } catch (e) {
        // 写入失败不影响主流程，窗口仍可作为占位页跳转收银台
      }
      return payWindow
    },
    /** 关闭「处理中」提示页（下单失败/异常时调用；窗口已被用户关闭则忽略） */
    closeCashierLoadingWindow(payWindow) {
      try {
        if (payWindow && !payWindow.closed) {
          payWindow.close()
        }
      } catch (e) {
        // 跨域或已关闭等异常直接忽略
      }
    },
    /** HTML 转义：师傅姓名等用户数据拼入提示页前先转义，防止破坏页面结构 */
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    /** 刷新详情弹窗与列表（行级下单/支付后同步状态） */
    reloadDetail(orderId) {
      if (!orderId) return
      insuranceOrderApi.getUnderwritingDetail({ id: orderId }).then(res => {
        if (res && res.success) {
          this.detailForm = res.data || {}
        }
      })
      this.getList()
    },
    /** 点击收银台：新窗口打开 + 启动轮询，检测到目标明细付款状态=已付款(2) 时自动刷新弹窗与列表 */
    openCashierAndPoll(record, url) {
      if (url) {
        window.open(url, '_blank')
      }
      // record.insuranceOrderId 是保险订单 id；record.id 是明细 id（用于精确定位轮询目标）
      if (record && record.insuranceOrderId && record.id) {
        this.startDetailPoll(record.insuranceOrderId, record.id)
        // 同时加入列表级轮询：用户关掉弹窗去支付时列表也能自动刷新
        this.addPendingPolicy(record.insuranceOrderId, record.id)
      }
    },
    startDetailPoll(orderId, targetPolicyId) {
      this.stopDetailPoll()
      const that = this
      const startedAt = Date.now()
      // 记录上一次详情快照，仅在「状态/保单URL 发生变化」时才刷新视图与列表，避免无意义重渲染
      let lastSnapshot = ''
      let notified = false
      const tick = () => {
        // 弹窗已关闭则停掉轮询，避免泄漏
        if (!that.detailVisible) {
          that.stopDetailPoll()
          return
        }
        // 单次轮询最长 10 分钟，防止异常下单导致无限轮询
        if (Date.now() - startedAt > that.detailPollMaxAge) {
          that.stopDetailPoll()
          return
        }
        insuranceOrderApi.getUnderwritingDetail({ id: orderId })
          .then(res => {
            if (!res || !res.success || !res.data) return
            const data = res.data
            const newList = Array.isArray(data.policyList) ? data.policyList : []
            const target = newList.find(p => Number(p.id) === Number(targetPolicyId))
            const snapshot = target
              ? `${target.underwritingStatus || ''}_${target.insurerPayStatus || ''}_${target.policyFileUrl || ''}`
              : ''
            if (snapshot !== lastSnapshot) {
              // 状态或保单文件发生变化：同步刷详情与列表
              that.detailForm = data
              that.getList()
              lastSnapshot = snapshot
            }
            // 终态：投保成功 + 已付款 + 保单文件 三者齐备
            if (!notified && that.isPolicyTerminal(target)) {
              that.$message.success('收银台支付成功，已自动刷新')
              notified = true
              that.stopDetailPoll()
            }
          })
          .catch(() => { /* 网络异常静默，下一轮继续 */ })
      }
      // 立即先跑一次（不等 3 秒），UX 更跟手
      tick()
      this.detailPollTimer = setInterval(tick, this.detailPollInterval)
    },
    stopDetailPoll() {
      if (this.detailPollTimer) {
        clearInterval(this.detailPollTimer)
        this.detailPollTimer = null
      }
    },
    /**
     * 判断明细行是否达到终态：投保成功(1200) + 已付款(2) + 保单文件 URL
     * 三者齐备才算真正"支付成功 + 出单完成"，与后台 TRADE_SUCCESS 回调落库口径一致
     */
    isPolicyTerminal(policy) {
      return !!policy
        && Number(policy.underwritingStatus) === 1200
        && Number(policy.insurerPayStatus) === 2
        && !!policy.policyFileUrl
    },
    /**
     * 加入一条待跟踪明细；去重后启动列表轮询（仅一次）
     */
    addPendingPolicy(orderId, policyId) {
      if (!orderId || !policyId) return
      const exists = this.pendingPolicies.some(p => p.orderId === orderId && p.policyId === policyId)
      if (exists) return
      this.pendingPolicies.push({ orderId, policyId, startedAt: Date.now() })
      this.startListPollIfNeeded()
    },
    /** 列表页轮询：如有待跟踪明细则启动；同一时刻最多一个 timer */
    startListPollIfNeeded() {
      if (this.listPollTimer) return
      if (this.pendingPolicies.length === 0) return
      this.listPollTimer = setInterval(() => {
        this.tickListPoll()
      }, this.listPollInterval)
    },
    stopListPoll() {
      if (this.listPollTimer) {
        clearInterval(this.listPollTimer)
        this.listPollTimer = null
      }
    },
    /**
     * 列表轮询 tick：
     * - 过期清理（超过 5 分钟视为放弃等待）
     * - 按 orderId 分组拉详情，避免对同一订单重复请求
     * - 同步刷详情（若对应订单的详情弹窗开着）
     * - 已达终态的明细从 pendingPolicies 中移除
     * - 所有明细都终态 / pendingPolicies 清空 → 自动停止轮询
     */
    tickListPoll() {
      if (this.pendingPolicies.length === 0) {
        this.stopListPoll()
        return
      }
      const now = Date.now()
      // 过期清理
      this.pendingPolicies = this.pendingPolicies.filter(p => now - p.startedAt < this.listPollMaxAge)
      if (this.pendingPolicies.length === 0) {
        this.stopListPoll()
        return
      }
      const orderIds = Array.from(new Set(this.pendingPolicies.map(p => p.orderId)))
      const requests = orderIds.map(id =>
        insuranceOrderApi.getUnderwritingDetail({ id }).catch(() => null)
      )
      Promise.all(requests).then(responses => {
        let touched = false
        const reachedTerminal = []
        for (let i = 0; i < responses.length; i++) {
          const res = responses[i]
          const orderId = orderIds[i]
          if (!res || !res.success || !res.data) continue
          const data = res.data
          // 若对应订单的详情弹窗正开着，同步更新弹窗内容
          if (this.detailVisible && this.detailForm && Number(this.detailForm.id) === Number(data.id)) {
            this.detailForm = data
            touched = true
          }
          const policies = Array.isArray(data.policyList) ? data.policyList : []
          for (const p of this.pendingPolicies) {
            if (p.orderId !== orderId) continue
            const target = policies.find(x => Number(x.id) === Number(p.policyId))
            if (this.isPolicyTerminal(target)) {
              reachedTerminal.push(`${p.orderId}_${p.policyId}`)
              touched = true
            }
          }
        }
        if (reachedTerminal.length > 0) {
          this.pendingPolicies = this.pendingPolicies.filter(
            p => !reachedTerminal.includes(`${p.orderId}_${p.policyId}`)
          )
        }
        if (touched) {
          this.getList()
        }
        if (this.pendingPolicies.length === 0) {
          this.stopListPoll()
        }
      })
    },
    openUploadModal(record) {
      this.uploadVisible = true
      this.uploadPolicyList = Array.isArray(record.policyList) ? record.policyList.slice() : []
      const firstPolicy = this.uploadPolicyList[0] || null
      const multiInsured = this.uploadPolicyList.length > 1
      this.uploadForm = {
        id: record.id,
        policyId: multiInsured ? null : (firstPolicy ? firstPolicy.id : null),
        policyFileUrl: multiInsured ? '' : (firstPolicy ? (firstPolicy.policyFileUrl || '') : (record.policyFileUrl || '')),
        policyFileName: multiInsured ? '' : (firstPolicy ? (firstPolicy.policyFileName || '') : (record.policyFileName || '')),
        manualRemark: record.manualRemark || ''
      }
      this.uploadFileList = this.buildFileList(this.uploadForm.policyFileUrl, this.uploadForm.policyFileName)
    },
    closeUploadModal() {
      this.uploadVisible = false
      this.uploadSubmitting = false
      this.uploadForm = {
        id: null,
        policyId: null,
        policyFileUrl: '',
        policyFileName: '',
        manualRemark: ''
      }
      this.uploadFileList = []
      this.uploadPolicyList = []
    },
    formatPolicyOption(item) {
      const name = item && item.insuredName ? item.insuredName : '未命名被保人'
      const card = item && item.insuredIdCard ? item.insuredIdCard : '无身份证'
      const status = item && item.policyFileUrl ? '已上传' : '未上传'
      return `${name} / ${card} / ${status}`
    },
    /** 明细行投保状态文案：0-未触发 1000-待投保 1100-投保中 1200-成功 1300-失败待重试 1400-失败 1500-已取消 1600-待核对 */
    policyUnderwritingStatusText(status) {
      const map = {
        0: '未触发',
        1000: '待投保',
        1100: '投保中',
        1200: '投保成功',
        1300: '失败待重试',
        1400: '投保失败',
        1500: '已取消',
        1600: '待核对'
      }
      const key = Number(status)
      return map[key] || '—'
    },
    policyUnderwritingStatusColor(status) {
      const key = Number(status)
      if (key === 1200) return 'green'
      // 待核对属于「不确定」而非失败：单独用橙色，避免运营误当成失败去重试
      if (key === 1600) return 'orange'
      if (key === 1300 || key === 1400 || key === 1500) return 'red'
      if (key === 1100 || key === 1000) return 'blue'
      return 'default'
    },
    /** 付款状态文案：0-待付款 1-付款中 2-已付款 3-已退款 4-部分已付款(仅主单聚合值) */
    policyInsurerPayStatusText(status) {
      const map = { 0: '待付款', 1: '付款中', 2: '已付款', 3: '已退款', 4: '部分已付款' }
      const key = Number(status)
      return map[key] || '—'
    },
    policyInsurerPayStatusColor(status) {
      const key = Number(status)
      if (key === 2) return 'green'
      if (key === 0) return 'orange'
      if (key === 1) return 'blue'
      if (key === 3) return 'purple'
      if (key === 4) return 'gold'
      return 'default'
    },
    handleUploadPolicyChange(policyId) {
      const policy = this.uploadPolicyList.find(item => item.id === policyId)
      this.uploadForm.policyFileUrl = policy ? (policy.policyFileUrl || '') : ''
      this.uploadForm.policyFileName = policy ? (policy.policyFileName || '') : ''
      this.uploadFileList = this.buildFileList(this.uploadForm.policyFileUrl, this.uploadForm.policyFileName)
    },
    handlePolicyFileChange(fileList) {
      if (!Array.isArray(fileList) || fileList.length === 0) {
        this.uploadForm.policyFileUrl = ''
        this.uploadForm.policyFileName = ''
        this.uploadFileList = []
        return
      }
      const firstFile = fileList[0]
      const fileUrl = firstFile.url || firstFile.path || firstFile.halfUrl || ''
      const fileName = firstFile.name || this.extractFileName(fileUrl)
      if (!this.isPdfFile(fileName) && !this.isPdfFile(fileUrl)) {
        this.$message.warning('请上传 PDF 格式的保单文件')
        this.uploadForm.policyFileUrl = ''
        this.uploadForm.policyFileName = ''
        this.uploadFileList = []
        return
      }
      this.uploadForm.policyFileUrl = fileUrl
      this.uploadForm.policyFileName = fileName
      this.uploadFileList = fileList
    },
    submitUploadPolicy() {
      if (this.uploadPolicyList.length > 0 && !this.uploadForm.policyId) {
        this.$message.warning('多人订单请先选择被保人后再上传保单')
        return
      }
      if (this.uploadPolicyList.length === 0 && !this.uploadForm.id) {
        this.$message.warning('保险订单数据异常')
        return
      }
      if (!this.uploadForm.policyFileUrl) {
        this.$message.warning('请先上传保单 PDF')
        return
      }
      if (!this.isPdfFile(this.uploadForm.policyFileName) && !this.isPdfFile(this.uploadForm.policyFileUrl)) {
        this.$message.warning('请上传 PDF 格式的保单文件')
        return
      }
      const currentId = this.uploadForm.id
      this.uploadSubmitting = true
      insuranceOrderApi.uploadInsurancePolicy(this.uploadForm)
        .then(() => {
          this.$message.success('保单回传成功')
          this.closeUploadModal()
          this.getList()
          if (this.detailVisible && this.detailForm.id === currentId) {
            this.openDetail({ id: currentId })
          }
        })
        .finally(() => {
          this.uploadSubmitting = false
        })
    },
    openStatusModal(record) {
      this.statusVisible = true
      // 人工可设置档位仅 1000/1100/1400/1500；1200(已成功)、1250(部分成功，聚合值) 等不在其中，默认落到「投保中」
      const selectableStatus = [1000, 1100, 1400, 1500]
      const currentStatus = Number(record.underwritingStatus)
      this.statusForm = {
        id: record.id,
        underwritingStatus: selectableStatus.includes(currentStatus) ? currentStatus : 1100,
        lastFailReason: record.lastFailReason || '',
        manualRemark: record.manualRemark || ''
      }
    },
    closeStatusModal() {
      this.statusVisible = false
      this.statusSubmitting = false
      this.statusForm = {
        id: null,
        underwritingStatus: 1100,
        lastFailReason: '',
        manualRemark: ''
      }
    },
    submitManualStatus() {
      if (!this.statusForm.underwritingStatus) {
        this.$message.warning('请选择投保状态')
        return
      }
      if (this.statusForm.underwritingStatus === 1400 && !this.statusForm.lastFailReason) {
        this.$message.warning('投保失败时请填写失败原因')
        return
      }
      const currentId = this.statusForm.id
      this.statusSubmitting = true
      insuranceOrderApi.updateInsuranceManualStatus(this.statusForm)
        .then(() => {
          this.$message.success('投保状态更新成功')
          this.closeStatusModal()
          this.getList()
          if (this.detailVisible && this.detailForm.id === currentId) {
            this.openDetail({ id: currentId })
          }
        })
        .finally(() => {
          this.statusSubmitting = false
        })
    },
    viewPolicy(record) {
      const url = record && record.policyFileUrl
      if (!url) {
        this.$message.warning('暂无保单文件')
        return
      }
      window.open(url, '_blank')
    },
    buildFileList(url, name) {
      if (!url) {
        return []
      }
      return [{
        uid: String(Date.now()),
        name: name || this.extractFileName(url),
        status: 'done',
        url,
        path: url
      }]
    },
    extractFileName(url) {
      if (!url) {
        return ''
      }
      const cleanUrl = String(url).split('?')[0].split('#')[0]
      const segments = cleanUrl.split('/')
      return segments[segments.length - 1] || ''
    },
    isPdfFile(value) {
      const fileName = this.extractFileName(value)
      return !!fileName && fileName.toLowerCase().endsWith('.pdf')
    }
  }
}
</script>

<style lang="less" scoped>
.table-page-search-wrapper {
  margin-bottom: 16px;
}

.table-operations {
  margin-bottom: 16px;
}

.ant-table-pagination {
  margin-top: 16px;
  text-align: right;
}

/* 财务待投保高亮行：师傅已抢单但未投保下单（动态计算，全部投保后自动消失） */
/deep/ .ant-table-tbody > tr.finance-highlight-row > td {
  background-color: #fffbe6 !important;
}
/deep/ .ant-table-tbody > tr.finance-highlight-row:hover > td {
  background-color: #ffe58f !important;
}

.upload-hint {
  margin-top: 8px;
  color: #999;
  font-size: 12px;
}

.insurance-detail-body {
  .insurance-detail-section + .insurance-detail-section {
    margin-top: 20px;
  }
}

.insurance-detail-section__title {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}

.insurance-detail-desc /deep/ .ant-descriptions-view table {
  table-layout: fixed;
  width: 100%;
}

.insurance-detail-desc /deep/ .ant-descriptions-item-label {
  white-space: normal !important;
  word-break: keep-all;
  writing-mode: horizontal-tb;
}

.insurance-detail-desc /deep/ .ant-descriptions-item-content {
  word-break: break-all;
  vertical-align: top;
}

.insurance-detail-empty {
  color: rgba(0, 0, 0, 0.25);
}

.insurance-detail-snapshot + .insurance-detail-snapshot {
  margin-top: 12px;
}

.insurance-detail-snapshot__label {
  margin-bottom: 6px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}

.policy-summary {
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.65);
}

.detail-policy-action {
  margin-top: 12px;
}

.policy-table {
  margin-top: 8px;
}

.policy-card-no,
.policy-upload-time {
  margin-top: 4px;
  color: #999;
  font-size: 12px;
}

/* 收银台剩余有效期提示（随支付剩余时间由服务端过期时间推算） */
.policy-cashier-remain {
  font-style: normal;
  color: #fa8c16;
}

.detail-insured-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-insured-item {
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}

.detail-insured-item__head {
  margin-bottom: 8px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
}

.detail-insured-item__row {
  margin-top: 4px;
  line-height: 1.6;
  word-break: break-all;
}

.detail-insured-label {
  display: inline-block;
  min-width: 52px;
  margin-right: 8px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}

.detail-insured-value {
  font-size: 13px;
}
</style>
